import type { Me, Role, WorkspaceKey } from '../../api';
import type { DictKey } from '../../i18n';
import { canSee, type NavItem, type Section } from './nav';

/**
 * Робочі простори (фаза 2) — дзеркало реєстру API xq/workspaces.py. Кожен простір — коротке меню з наявних сторінок.
 * Доступ: роль людини серед типових ролей простору (дзеркало `roles` в API) або явний допуск (me.workspaces).
 */
export interface WorkspaceDef {
  key: WorkspaceKey;
  titleKey: DictKey;
  landing: string;
  roles: Role[];
  menu: NavItem[];
}

const home = (key: WorkspaceKey): NavItem => ({ section: 'workspaceHome', to: `/w/${key}`, key: 'ws.menu.home' });
const today: NavItem = { section: 'today', to: '/today', key: 'ws.menu.today' };

export const WORKSPACES: WorkspaceDef[] = [
  {
    key: 'sales',
    titleKey: 'ws.title.sales',
    landing: '/w/sales',
    roles: ['owner', 'manager', 'operator'],
    menu: [
      home('sales'),
      today,
      { section: 'requests', to: '/leads', key: 'ws.menu.leads' },
      { section: 'signals', to: '/signals', key: 'nav.signals', match: ['/parser'] },
      { section: 'inbox', to: '/inbox', key: 'ws.menu.inbox' },
      { section: 'tasks', to: '/tasks', key: 'ws.menu.tasks' },
    ],
  },
  {
    key: 'cases',
    titleKey: 'ws.title.cases',
    landing: '/w/cases',
    roles: ['owner', 'manager', 'operator', 'accountant'],
    menu: [
      home('cases'),
      today,
      { section: 'cases', to: '/cases', key: 'ws.menu.cases' },
      { section: 'waiting', to: '/waiting', key: 'nav.waiting' },
      { section: 'tasks', to: '/tasks', key: 'ws.menu.tasks' },
      { section: 'documents', to: '/documents', key: 'ws.menu.documents', match: ['/my-documents'] },
      { section: 'myDocuments', to: '/my-documents', key: 'ws.menu.documents', onlyWithout: 'documents' },
      { section: 'planner', to: '/planner', key: 'ws.menu.planner' },
      // інструкції країн потрібні менеджерам справ щодня, не лише в налаштуваннях
      { section: 'countries', to: '/countries', key: 'ws.menu.countries' },
    ],
  },
  {
    key: 'ritual',
    titleKey: 'ws.title.ritual',
    landing: '/w/ritual',
    roles: ['owner', 'manager', 'operator'],
    menu: [
      home('ritual'),
      { section: 'ritual', to: '/ritual', key: 'ws.menu.ritual' },
      { section: 'catalog', to: '/products', key: 'ws.menu.products' },
    ],
  },
  {
    key: 'transport',
    titleKey: 'ws.title.transport',
    landing: '/w/transport',
    roles: ['owner', 'manager', 'operator', 'driver'],
    menu: [
      home('transport'),
      today,
      { section: 'fleet', to: '/fleet', key: 'ws.menu.fleet', match: ['/vehicles', '/trips'] },
      { section: 'handovers', to: '/handovers', key: 'ws.menu.handovers' },
      { section: 'partners', to: '/settings/partners', key: 'ws.menu.partners' },
    ],
  },
  {
    key: 'production',
    titleKey: 'ws.title.production',
    landing: '/w/production',
    roles: ['owner', 'manager', 'operator'],
    menu: [
      home('production'),
      { section: 'freight', to: '/freight', key: 'ws.menu.freight' },
      { section: 'inventory', to: '/inventory', key: 'ws.menu.inventory' },
      { section: 'orders', to: '/orders', key: 'ws.menu.orders', match: ['/b2b'] },
      { section: 'catalog', to: '/products', key: 'ws.menu.products', match: ['/collections', '/pricing'] },
    ],
  },
  {
    key: 'marketing',
    titleKey: 'ws.title.marketing',
    landing: '/w/marketing',
    roles: ['owner', 'manager'],
    menu: [
      home('marketing'),
      { section: 'marketing', to: '/marketing', key: 'ws.menu.marketing' },
      { section: 'requests', to: '/leads', key: 'ws.menu.leads' },
      { section: 'channels', to: '/channels', key: 'ws.menu.channels' },
    ],
  },
  {
    key: 'money',
    titleKey: 'ws.title.money',
    landing: '/w/money',
    roles: ['owner', 'accountant'],
    menu: [
      home('money'),
      { section: 'money', to: '/money', key: 'ws.menu.money' },
      { section: 'money', to: '/money?tab=invoices', key: 'ws.menu.invoices', match: ['/money/invoices'] },
      { section: 'orders', to: '/orders', key: 'ws.menu.orders', match: ['/b2b'] },
      { section: 'pricing', to: '/pricing', key: 'ws.menu.pricing' },
      { section: 'audit', to: '/settings/audit', key: 'ws.menu.audit' },
    ],
  },
  {
    key: 'quality',
    titleKey: 'ws.title.quality',
    landing: '/w/quality',
    roles: ['owner', 'manager'],
    menu: [
      home('quality'),
      { section: 'quality', to: '/quality', key: 'ws.menu.quality' },
      { section: 'reports', to: '/reports', key: 'nav.reports' },
    ],
  },
  {
    key: 'overview',
    titleKey: 'ws.title.overview',
    landing: '/overview',
    roles: ['owner'],
    menu: [
      { section: 'overview', to: '/overview', key: 'ws.menu.overview' },
      { section: 'control', to: '/control', key: 'ws.menu.control' },
    ],
  },
  {
    key: 'settings',
    titleKey: 'ws.title.settings',
    landing: '/settings',
    roles: ['owner'],
    menu: [
      { section: 'users', to: '/settings', key: 'ws.menu.users', match: ['/settings/users', '/settings/security'] },
      { section: 'users', to: '/settings/workspaces', key: 'ws.menu.workspaces' },
      { section: 'users', to: '/settings/sites', key: 'ws.menu.sites' },
      { section: 'users', to: '/settings/tgbot', key: 'tgbot.tab' },
      { section: 'reports', to: '/reports', key: 'nav.reports' },
      { section: 'processes', to: '/processes', key: 'ws.menu.processes' },
      { section: 'countries', to: '/countries', key: 'ws.menu.countries' },
      { section: 'dictionaries', to: '/dictionaries', key: 'ws.menu.dictionaries' },
      { section: 'vehicleTypes', to: '/vehicle-types', key: 'ws.menu.vehicleTypes' },
    ],
  },
];

export const WORKSPACE_BY_KEY: Record<WorkspaceKey, WorkspaceDef> = Object.fromEntries(
  WORKSPACES.map((w) => [w.key, w]),
) as Record<WorkspaceKey, WorkspaceDef>;

/** Розділи нових просторів, які відкриває явний допуск до простору (а не лише роль). */
export const SECTION_WORKSPACE: Partial<Record<Section, WorkspaceKey>> = {
  overview: 'overview',
  ritual: 'ritual',
  quality: 'quality',
  marketing: 'marketing',
  money: 'money',
};

export function isWorkspaceKey(k: string | null | undefined): k is WorkspaceKey {
  return !!k && k in WORKSPACE_BY_KEY;
}

/** Ключі моїх просторів: з /auth/me, а для старого API — типові для ролі. */
export function myWorkspaceKeys(me: Pick<Me, 'role' | 'workspaces'> | null | undefined): WorkspaceKey[] {
  if (!me) return [];
  if (me.workspaces) return me.workspaces.map((w) => w.key);
  return WORKSPACES.filter((w) => w.roles.includes(me.role)).map((w) => w.key);
}

/** Шлях (можна з ?query) під пунктом меню. Пункт з ?query — лише той самий шлях з усіма його параметрами. */
function matches(path: string, prefix: string): boolean {
  const [p, q = ''] = path.split('?');
  const [base, want] = prefix.split('?');
  if (want !== undefined) {
    if (p !== base) return false;
    const have = new URLSearchParams(q);
    return [...new URLSearchParams(want)].every(([k, v]) => have.get(k) === v);
  }
  if (base === '/') return p === '/';
  return p === base || p!.startsWith(`${base}/`);
}

/** Меню простору для людини: лише пункти, які вона бачить за роллю або допуском. */
export function menuFor(key: WorkspaceKey | null | undefined, me: Pick<Me, 'role' | 'workspaces'> | null | undefined): NavItem[] {
  const ws = key ? WORKSPACE_BY_KEY[key] : undefined;
  if (!ws || !me) return [];
  const grants = myWorkspaceKeys(me);
  return ws.menu.filter(
    (it) => canSee(me.role, it.section, grants) && !(it.onlyWithout && canSee(me.role, it.onlyWithout, grants)),
  );
}

/** Пункт меню простору для шляху: найдовший збіг (щоб «/money/invoices» не підсвічував «/money»). */
export function menuItemFor(key: WorkspaceKey | null | undefined, path: string, items?: NavItem[]): NavItem | null {
  const list = items ?? (key ? WORKSPACE_BY_KEY[key]?.menu : undefined) ?? [];
  let best: NavItem | null = null;
  let bestLen = -1;
  for (const it of list) {
    for (const p of [it.to, ...(it.match ?? [])]) {
      if (matches(path, p) && p.length > bestLen) {
        best = it;
        bestLen = p.length;
      }
    }
  }
  return best;
}

/** Перший простір (з `keys`, типово — усі), меню якого містить шлях. */
export function workspaceOfPath(path: string, keys?: readonly WorkspaceKey[]): WorkspaceKey | null {
  const w = /^\/w\/(\w+)/.exec(path);
  if (w && isWorkspaceKey(w[1]) && (!keys || keys.includes(w[1]))) return w[1];
  for (const ws of WORKSPACES) {
    if (keys && !keys.includes(ws.key)) continue;
    if (menuItemFor(ws.key, path)) return ws.key;
  }
  return null;
}

/** Куди вести людину після входу і з «/»: головна поточного простору. */
export function landingFor(me: Pick<Me, 'role' | 'workspaces' | 'workspace'> | null | undefined): string {
  const keys = myWorkspaceKeys(me);
  const key =
    me?.workspace && keys.includes(me.workspace) ? me.workspace : keys.includes('overview') ? 'overview' : keys[0];
  if (key) return WORKSPACE_BY_KEY[key].landing;
  return '/today';
}
